import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, Link as RouterLink, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

// material-ui
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import Grid from '@material-ui/core/Grid';
import AcceptIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import BackIcon from '@material-ui/icons/KeyboardArrowLeft';
import RestoreIcon from '@material-ui/icons/Restore';
import DeleteIcon from '@material-ui/icons/DeleteForever';
import Skeleton from '@material-ui/lab/Skeleton';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/lab/Alert';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';

// local
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';
import FormatPhoneNumber from 'app/utils/FormatPhoneNumber';
import DetailSkeleton from 'app/components/loaders/DetailSkeleton';
import GroupSelect from 'app/components/group/GroupSelect';
import { containerFlex, containerFlexFixed, containerFlexGrow } from 'app/styles/new-layout';


const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3),
    },
    content: {
        ...containerFlexGrow(theme),
        position: 'relative',
        paddingLeft: theme.spacing(3),

        [theme.breakpoints.down('md')]: {
            paddingLeft: '0px',
        }
    },
    nav: {
        ...containerFlexFixed(theme),
    },
    paperMembership: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    messagePaper: {
        padding: theme.spacing(3)
    },
    messageGrid: {
        [theme.breakpoints.down('sm')]: {
            direction: 'column'
        }
    },
    link: {
        textDecoration: 'none'
    },
    emptyCon: {
        padding: theme.spacing(3),
        textAlign: 'center'
    },
    section: {
        marginTop: theme.spacing(3)
    },
    sectionCenter: {
        textAlign: 'center'
    },
    alert: {
        marginBottom: theme.spacing(3),
        color: 'red'
    },
    menuItem: {
        borderRadius: theme.shape.borderRadius
    },
    skeleton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    }
}));

export default function AccessRequestDetails(props) {

    const [ request, setRequest ] = useState(null);
    const [ requestLoading, setRequestLoading ] = useState(false);
    const [ user, setUser ] = useState(null);
    const [ groupSelectDialogOpen, setGroupSelectDialogOpen ] = useState(false);
    const [ levelID, setLevelID ] = useState(0);


    const { app } = useApp();
    const api = useApi();
    const classes = useStyles();
    const params = useParams();
    const history = useHistory();


    const getRequest = () => {
        setRequestLoading(true);
        api.get(`/admin/club/access-requests/${params.id}`)
            .then(res => {
                // console.log(res)
                setRequest(res.request)
                setUser(res.request.user)
            })
            .finally(() => setRequestLoading(false))
    }

    useEffect(() => {
        getRequest();
    }, []);

    // Poseter Functions

    const postAccept = () => {
        setRequestLoading(true);
        api.post(`/admin/club/accept-access`, {
            id: params.id,
            level_id: levelID
        })
            .then(() => {
                setGroupSelectDialogOpen(false);
                history.push('/admin/access-requests')
            })
            .catch(setRequestLoading(false))
    }

    const postReject = () => {
        setRequestLoading(true);
        api.post(`/admin/club/reject-access`, {id: params.id})
            .then(getRequest)
            .finally(() => setRequestLoading(false))
    }

    const postRestore = () => {
        setRequestLoading(true);
        api.post(`/admin/club/restore-access`, {id: params.id})
            .then(getRequest)
            .finally(() => setRequestLoading(false))
    }

    const postDelete = () => {
        setRequestLoading(true);
        api.post(`/admin/club/delete-access`, {id: params.id})
            .then(getRequest)
            .finally(() => setRequestLoading(false))
    }

    // Dialog Functions

    const handleDialogOpen = () => {
        setGroupSelectDialogOpen(true);
    }

    const handleDialogClose = () => {
        setGroupSelectDialogOpen(false);
    }

    const handleGroupSelect = (id) => {
        setLevelID(id);
    }

    // Helper Functions

    const hasHomeAddress = () => {
        if (user && user.address_one && user.city && user.state && user.zip) return true;
        return false;
    }

    const hasWorkAddress = () => {
        if (user && user.work_address_one && user.work_city && user.work_state && user.work_zip) return true;
        return false;
    }

    const hasPersonal = () => {
        if (user && (user.marital_status || user.nickname)) return true;
        return false;
    }

    const hasWork = () => {
        if (user && (user.industry || user.employer || user.position )) return true;
        return false;
    }

    const hasContact = () => {
        if (user && (
            user.email ||
            user.phone_home ||
            user.phone_mobile ||
            user.phone_work)
        ) return true;

        return false;
    }

    return (
        <div className={classes.container}>
            <div className={classes.nav}>
                <Typography color="textSecondary" align="center" component="div">
                    <HowToRegIcon style={{ fontSize: 180 }} color="inherit" />
                    <Typography variant="h4" color="inherit"gutterBottom >
                        Access Request
                    </Typography>
                </Typography>
                <Divider />
                {(request && request.deleted_at) ? (
                    <MenuList>
                        <MenuItem
                            onClick={postRestore}
                            className={classes.menuItem}
                            >
                            <ListItemIcon>
                                <RestoreIcon />
                            </ListItemIcon>
                            <ListItemText primary="Restore Request" />
                        </MenuItem>
                        <MenuItem
                            onClick={postDelete}
                            className={classes.menuItem}
                            >
                            <ListItemIcon>
                                <DeleteIcon />
                            </ListItemIcon>
                            <ListItemText primary="Delete Permanently" />
                        </MenuItem>
                        <MenuItem
                            to="/admin/access-requests?tab=rejected"
                            component={RouterLink}
                            className={classes.menuItem}
                            >
                            <ListItemIcon>
                                <BackIcon />
                            </ListItemIcon>
                            <ListItemText primary="Back To Requests" />
                        </MenuItem>
                    </MenuList>
                ) : (
                    <MenuList>
                        <MenuItem
                            onClick={handleDialogOpen}
                            className={classes.menuItem}
                            >
                            <ListItemIcon>
                                <AcceptIcon />
                            </ListItemIcon>
                            <ListItemText primary="Accept Request" />
                        </MenuItem>
                        <MenuItem
                            onClick={postReject}
                            className={classes.menuItem}
                            >
                            <ListItemIcon>
                                <CloseIcon />
                            </ListItemIcon>
                            <ListItemText primary="Reject Request" />
                        </MenuItem>
                        <MenuItem
                            to="/admin/access-requests/"
                            component={RouterLink}
                            className={classes.menuItem}
                            >
                            <ListItemIcon>
                                <BackIcon />
                            </ListItemIcon>
                            <ListItemText primary="Back To Requests" />
                        </MenuItem>
                    </MenuList>
                )}
            </div>
            <div className={classes.content}>
                {(request && request.deleted_at) &&
                    <Alert severity="error" className={classes.alert}>
                        This request has been rejected!  You can restore it or delete permanently.
                    </Alert>
                }
                <Paper className={classes.paperMembership} >
                    {requestLoading ? (
                        <div className={classes.skeleton}>
                            <Skeleton animation="wave" height={20} width="60%"  />
                        </div>
                    ) : ( request &&
                        <Grid container direction="column" spacing={1} justify="center">
                            <Grid item>
                                <Grid container direction="row" spacing={2} justify="center">
                                    <Grid item>
                                        <Typography variant="subtitle1">
                                            Requested On:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1" color="textSecondary">
                                            {DateTime.fromISO(request.created_at).toLocaleString(DateTime.DATE_MED)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container direction="row" spacing={2} justify="center">
                                    <Grid item>
                                        <Typography variant="subtitle1">
                                            Message:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1" color="textSecondary">
                                            {request.meta.notes}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Paper>
                {requestLoading ? (<DetailSkeleton />) : user ? (
                    <Paper className={classes.paperMembership} >
                        <Fragment>
                            <div className={classes.sectionCenter}>
                                <Typography variant="h6" component="h3" id="user-dialogue-title">
                                    {user.full_name}
                                </Typography>
                                <Typography variant="subtitle1" component="h4" color="textSecondary">
                                    {user.public_membership_title}
                                </Typography>
                            </div>
                            <div className={classes.section}>
                                <Typography variant="subtitle1" component="h4" gutterBottom>
                                    Personal Information
                                </Typography>
                                { (hasPersonal()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        { (user.marital_status && user.marital_status != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Marital Status:</Grid>
                                                <Grid item><strong>
                                                    {user.marital_status}{user.spouse ? ' - ' + user.spouse : ""}
                                                </strong></Grid>
                                            </Grid>
                                        }
                                        { (user.nickname && user.nickname != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Nickname:</Grid>
                                                <Grid item><strong>{user.nickname}</strong></Grid>
                                            </Grid>
                                        }
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No contact information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.section}>
                                <Typography variant="body1" component="h4" gutterBottom>
                                    Contact Information
                                </Typography>
                                { (hasContact()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        { (user.email && user.email != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                    <Grid item>Email:</Grid>
                                                    <Grid item>
                                                        <strong>
                                                            <Link
                                                                color="secondary"
                                                                href={'mailto:' + user.email}
                                                                >
                                                                {user.email}
                                                            </Link>
                                                        </strong>
                                                    </Grid>
                                            </Grid>
                                        }
                                        { (user.phone_home && user.phone_home != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                    <Grid item>Home Phone:</Grid>
                                                    <Grid item>
                                                        <strong>
                                                            <Link
                                                                color="secondary"
                                                                href={'tel:' + user.phone_home}
                                                                >
                                                                {FormatPhoneNumber(user.phone_home)}
                                                            </Link>
                                                        </strong>
                                                    </Grid>
                                            </Grid>
                                        }
                                        { (user.phone_work && user.phone_work != "") &&
                                            <Grid container spacing={3} justify="space-between">
                                                    <Grid item>Work Phone:</Grid>
                                                    <Grid item>
                                                        <strong>
                                                            <Link
                                                                color="secondary"
                                                                href={'tel:' + user.phone_work}
                                                                >
                                                                {FormatPhoneNumber(user.phone_work)}
                                                            </Link>
                                                        </strong>
                                                    </Grid>
                                            </Grid>
                                        }
                                        { (user.phone_mobile && user.phone_mobile != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                    <Grid item>Mobile Phone:</Grid>
                                                    <Grid item>
                                                        <strong>
                                                            <Link
                                                                color="secondary"
                                                                href={'tel:' + user.phone_mobile}
                                                                >
                                                                {FormatPhoneNumber(user.phone_mobile)}
                                                            </Link>
                                                        </strong>
                                                    </Grid>
                                            </Grid>
                                        }
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No contact information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.section}>
                                <Typography variant="body1" component="h4" gutterBottom>
                                    Home Address
                                </Typography>
                                { (hasHomeAddress()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        <Grid container spacing={1}>
                                            <Grid xs={12} item>
                                                {user.address_one}&nbsp;&nbsp;&nbsp;
                                                {user.address_two ? user.address_two : ""}<br/>
                                                {user.city}&nbsp;&nbsp;&nbsp;
                                                {user.state}<br/>
                                                {user.zip}
                                            </Grid>
                                        </Grid>
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No contact information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.section}>
                                <Typography variant="body1" component="h4" gutterBottom>
                                    Work Information
                                </Typography>
                                { (hasWork()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        { (user.industry && user.industry != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Industry:</Grid>
                                                <Grid item><strong>
                                                    {user.industry}
                                                </strong></Grid>
                                            </Grid>
                                        }
                                        { (user.employer && user.employer != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Employer:</Grid>
                                                <Grid item><strong>{user.employer}</strong></Grid>
                                            </Grid>
                                        }
                                        { (user.position && user.position != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Position:</Grid>
                                                <Grid item><strong>{user.position}</strong></Grid>
                                            </Grid>
                                        }
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No work information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.section}>
                                <Typography variant="body1" component="h4" gutterBottom>
                                    Work Address
                                </Typography>
                                {(hasWorkAddress()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        <Grid container spacing={1}>
                                            <Grid xs={12} item>
                                                {user.work_address_one}&nbsp;&nbsp;&nbsp;
                                                {user.work_address_two ? user.work_address_two : ""}<br/>
                                                {user.work_city}&nbsp;&nbsp;&nbsp;
                                                {user.work_state}<br/>
                                                {user.work_zip}
                                            </Grid>
                                        </Grid>
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No work address information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                        </Fragment>
                    </Paper>
                ) : (
                    <Typography
                        component="h3"
                        color="textSecondary"
                        variant="h6"
                        className={classes.emptyCon}
                        >
                        No matching guests found.
                    </Typography>
                )}
            </div>
            <Dialog
                open={groupSelectDialogOpen}
                onClose={handleDialogClose}
                fullWidth
                maxWidth="sm"
                aria-labelledby="select-level-title"
                >
                <DialogTitle id="select-level-title">
                    Select Membership Level
                </DialogTitle>
                <DialogContent>
                    <GroupSelect
                        variant='level'
                        onSelect={handleGroupSelect}
                        />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDialogClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={postAccept}>
                        Accept Request
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
